/**
 * useLocalEditState — ScheduleDraft 本地编辑工作副本与 Undo/Redo 状态。
 *
 * 本 hook 持有冻结的服务端基线、本地航班 Map、操作级 ChangeSet 栈和 dirty
 * diff。它不发起 HTTP 请求，只为编辑 Pipeline、保存流程和 ViewModel 投影提供
 * 本地状态 API。
 *
 * 设计文档 → docs/30_editing_publish/30_local_edit_and_undo.md
 * 骨架关联 → context/ScheduleDraftEditorWorkspaceContext.jsx (投影接入), hooks/useScheduleDraftEditPipeline.js (ChangeSet 消费), lib/workspace/buildScheduleDraftWorkspaceModels.js (preview 行消费)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ADD_FLIGHT, BATCH_ADD_FLIGHTS } from "../lib/editCommand";
import { normalizeStcTypeEmptyValue } from "../constants/scheduleDraftOptions";
import { normalizeTimeValue, withCalculatedFlightTimings } from "../lib/flightTimingUtils";

const MAX_UNDO_STACK_SIZE = 100;
const TIME_FIELDS = new Set(["std", "sta", "created_at", "updated_at"]);
const DIRTY_FIELDS = [
    "flight_number",
    "aircraft_reg",
    "departure_airport",
    "arrival_airport",
    "std",
    "sta",
    "stc_type",
    "is_fixed",
    "remarks",
    "flight_route_id",
    "block_time_minutes",
    "min_block_time_minutes",
    "display_order",
    "change_status",
];
const LOCAL_METADATA_PREFIX = "_";
const ADDED_CHANGE_STATUS = "ADDED";
const DELETED_CHANGE_STATUS = "DELETED";
const ADD_FLIGHT_CHANGE_TYPES = new Set([ADD_FLIGHT, BATCH_ADD_FLIGHTS]);
const LOCAL_DERIVED_TIMING_FIELDS = ["ground_time_minutes", "min_ground_time_minutes"];

function clonePlain(value) {
    if (value == null || typeof value !== "object") {
        return value;
    }

    if (value instanceof Date) {
        return value.toISOString();
    }

    return JSON.parse(JSON.stringify(value));
}

function deepFreeze(value) {
    if (!value || typeof value !== "object" || Object.isFrozen(value)) {
        return value;
    }

    Object.values(value).forEach((child) => deepFreeze(child));
    Object.freeze(value);
    return value;
}

function freezeSnapshot(value) {
    const cloned = clonePlain(value);
    return deepFreeze(cloned);
}

function normalizeComparableValue(field, value) {
    if (TIME_FIELDS.has(field)) {
        if (!value) {
            return value ?? null;
        }

        const normalized = normalizeTimeValue(value);
        return normalized === null ? null : normalized;
    }
    if (field === "stc_type") {
        return normalizeStcTypeEmptyValue(value);
    }

    return value ?? null;
}

function dirtyValuesEqual(field, left, right) {
    return normalizeComparableValue(field, left) === normalizeComparableValue(field, right);
}

function createLocalFlightId() {
    if (typeof window !== "undefined" && window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }

    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (char) => (
        Number(char) ^ (Math.floor(Math.random() * 16) & (15 >> (Number(char) / 4)))
    ).toString(16));
}

function getAllFlightFields(flight) {
    return Array.from(new Set([
        ...DIRTY_FIELDS,
        ...Object.keys(flight || {}).filter((field) => !field.startsWith(LOCAL_METADATA_PREFIX)),
    ]));
}

function isDeletedFlight(flight) {
    return String(flight?.change_status || "").toUpperCase() === DELETED_CHANGE_STATUS;
}

function buildLocalFlight(flight, serverFlight = flight) {
    const localFlight = clonePlain(flight) || {};
    const dirtyFields = new Set();

    DIRTY_FIELDS.forEach((field) => {
        if (!dirtyValuesEqual(field, localFlight[field], serverFlight?.[field])) {
            dirtyFields.add(field);
        }
    });

    localFlight._dirty = dirtyFields.size > 0;
    localFlight._dirtyFields = dirtyFields;
    return localFlight;
}

function buildFlightsMap(flights = [], serverFlightsMap = null) {
    const nextMap = new Map();
    flights.forEach((flight, index) => {
        const id = flight?.id ?? `local-${index}`;
        const key = String(id);
        nextMap.set(key, buildLocalFlight(flight, serverFlightsMap?.get(key) || flight));
    });
    return nextMap;
}

function buildFlightFromChangeSet(changeSet, valueKey, targetFlightId = null) {
    const flight = {};
    const fallbackFlightId = changeSet?.changes?.[0]?.flightId ?? changeSet?.changes?.[0]?.flight_id;
    const flightId = targetFlightId ?? fallbackFlightId ?? flight.id;
    (changeSet?.changes || []).forEach((change) => {
        if (!change?.field) {
            return;
        }
        if (flightId !== undefined && flightId !== null && String(change.flightId ?? change.flight_id) !== String(flightId)) {
            return;
        }
        flight[change.field] = clonePlain(change[valueKey]);
    });

    if (flightId === undefined || flightId === null) {
        return null;
    }

    return {
        ...flight,
        id: flight.id ?? flightId,
    };
}

function getChangeSetInsertionContext(changeSet) {
    return changeSet?.meta?.insertionContext || changeSet?.meta?.insertion || null;
}

function getChangeSetFlightIds(changeSet) {
    const flightIds = [];
    const seen = new Set();
    const appendFlightId = (flightId) => {
        if (flightId === undefined || flightId === null) {
            return;
        }
        const flightIdStr = String(flightId);
        if (seen.has(flightIdStr)) {
            return;
        }
        seen.add(flightIdStr);
        flightIds.push(flightIdStr);
    };

    (changeSet?.meta?.addedFlightIds || []).forEach(appendFlightId);
    (changeSet?.changes || []).forEach((change) => appendFlightId(change?.flightId ?? change?.flight_id));
    return flightIds;
}

function getInsertionAnchorId(insertionContext = {}) {
    const context = insertionContext || {};
    const anchorFlight = context.anchorFlight || context.anchor_flight;
    return context.anchorFlightId
        ?? context.anchor_flight_id
        ?? anchorFlight?.id
        ?? anchorFlight?.flight_id;
}

function insertFlightIntoMap(localFlightsMap, flightId, flight, insertionContext) {
    const mode = insertionContext?.mode;
    const anchorId = getInsertionAnchorId(insertionContext);
    if (!((mode === "before" || mode === "after") && anchorId != null)) {
        const nextMap = new Map(localFlightsMap);
        nextMap.set(flightId, flight);
        return nextMap;
    }

    const nextMap = new Map();
    let inserted = false;
    localFlightsMap.forEach((currentFlight, currentId) => {
        if (String(currentId) === String(flightId)) {
            return;
        }
        if (mode === "before" && String(currentId) === String(anchorId)) {
            nextMap.set(flightId, flight);
            inserted = true;
        }
        nextMap.set(currentId, currentFlight);
        if (mode === "after" && String(currentId) === String(anchorId)) {
            nextMap.set(flightId, flight);
            inserted = true;
        }
    });

    if (!inserted) {
        nextMap.set(flightId, flight);
    }
    return nextMap;
}

function buildFrozenServerMap(flights = []) {
    const nextMap = new Map();
    flights.forEach((flight, index) => {
        const id = flight?.id ?? `server-${index}`;
        nextMap.set(String(id), freezeSnapshot(flight));
    });
    return nextMap;
}

function getFlightId(flight, fallbackId = null) {
    const flightId = flight?.id ?? flight?.flight_id ?? fallbackId;
    return flightId === undefined || flightId === null ? null : String(flightId);
}

function rebuildLocalDerivedTimings(localFlightsMap, airportConstraintsMap = {}) {
    if (!localFlightsMap?.size) {
        return localFlightsMap;
    }

    const recalculatedFlightsById = new Map(
        withCalculatedFlightTimings(Array.from(localFlightsMap.values()), airportConstraintsMap)
            .map((flight, index) => [getFlightId(flight, `flight-${index}`), flight])
            .filter(([flightId]) => flightId),
    );

    const nextMap = new Map();
    localFlightsMap.forEach((flight, flightId) => {
        const recalculatedFlight = recalculatedFlightsById.get(getFlightId(flight, flightId));
        if (!recalculatedFlight) {
            nextMap.set(String(flightId), flight);
            return;
        }

        const nextFlight = { ...flight };
        LOCAL_DERIVED_TIMING_FIELDS.forEach((field) => {
            nextFlight[field] = recalculatedFlight[field] ?? null;
        });
        nextMap.set(String(flightId), nextFlight);
    });

    return nextMap;
}

function applyLocalFlightsToTracks(tracks = [], localFlights = []) {
    if (!Array.isArray(tracks) || !tracks.length) {
        return tracks;
    }

    const flightsById = new Map(localFlights.map((flight) => [String(flight.id), flight]));
    const flightsByTrackId = localFlights.reduce((accumulator, flight) => {
        if (!flight?.track_id) {
            return accumulator;
        }
        const key = String(flight.track_id);
        const existing = accumulator.get(key) || [];
        accumulator.set(key, [...existing, flight]);
        return accumulator;
    }, new Map());

    return tracks.map((track) => {
        const trackId = String(track?.id || "");
        const localTrackFlights = flightsByTrackId.get(trackId);
        if (localTrackFlights) {
            return {
                ...track,
                flights: localTrackFlights,
            };
        }

        return {
            ...track,
            flights: (track?.flights || []).map((flight) => flightsById.get(String(flight.id)) || flight),
        };
    });
}

function computeDirtyState(localFlightsMap, serverFlightsMap) {
    const dirtyFlightIds = new Set();
    const dirtyFieldsByFlight = new Map();
    const nextLocalFlightsMap = new Map();

    localFlightsMap.forEach((flight, flightId) => {
        const serverFlight = serverFlightsMap.get(String(flightId));
        const dirtyFields = new Set();

        if (!serverFlight) {
            getAllFlightFields(flight).forEach((field) => dirtyFields.add(field));
        } else {
            DIRTY_FIELDS.forEach((field) => {
                if (!dirtyValuesEqual(field, flight?.[field], serverFlight?.[field])) {
                    dirtyFields.add(field);
                }
            });
        }

        if (isDeletedFlight(flight) && !dirtyValuesEqual("change_status", flight?.change_status, serverFlight?.change_status)) {
            dirtyFields.add("change_status");
        }

        const nextFlight = {
            ...flight,
            _dirty: dirtyFields.size > 0,
            _dirtyFields: dirtyFields,
        };

        if (dirtyFields.size > 0) {
            dirtyFlightIds.add(String(flightId));
            dirtyFieldsByFlight.set(String(flightId), dirtyFields);
        }

        nextLocalFlightsMap.set(String(flightId), nextFlight);
    });

    return {
        localFlightsMap: nextLocalFlightsMap,
        dirtyState: {
            isDirty: dirtyFlightIds.size > 0,
            dirtyFlightIds,
            dirtyFieldsByFlight,
        },
    };
}

function applyChangeValues(localFlightsMap, changes = [], valueKey) {
    const nextMap = new Map(localFlightsMap);

    changes.forEach((change) => {
        const flightId = String(change.flightId ?? change.flight_id);
        const existing = nextMap.get(flightId);
        if (!existing || !change.field) {
            return;
        }

        nextMap.set(flightId, {
            ...existing,
            [change.field]: clonePlain(change[valueKey]),
        });
    });

    return nextMap;
}

function applyChangeSetValues(localFlightsMap, changeSet, valueKey) {
    if (ADD_FLIGHT_CHANGE_TYPES.has(changeSet?.type)) {
        const flightIds = getChangeSetFlightIds(changeSet);
        let nextMap = new Map(localFlightsMap);
        if (valueKey === "before") {
            flightIds.forEach((flightId) => nextMap.delete(String(flightId)));
            return nextMap;
        }

        flightIds.forEach((flightId) => {
            const flight = buildFlightFromChangeSet(changeSet, valueKey, flightId);
            if (flight) {
                nextMap = insertFlightIntoMap(nextMap, String(flightId), flight, getChangeSetInsertionContext(changeSet));
            }
        });
        return nextMap;
    }

    return applyChangeValues(localFlightsMap, changeSet?.changes || [], valueKey);
}

function pushUndoStack(stack, changeSet) {
    const nextStack = [...stack, changeSet];
    if (nextStack.length > MAX_UNDO_STACK_SIZE) {
        return nextStack.slice(nextStack.length - MAX_UNDO_STACK_SIZE);
    }
    return nextStack;
}

const EMPTY_DIRTY_STATE = {
    isDirty: false,
    dirtyFlightIds: new Set(),
    dirtyFieldsByFlight: new Map(),
};

/**
 * 管理 ScheduleDraft 本地编辑状态。
 * @returns {Object} 本地编辑状态与操作 API。
 */
export function useLocalEditState({ airportConstraintsMap = {} } = {}) {
    const serverStateRef = useRef(new Map());
    const localFlightsMapRef = useRef(new Map());
    const airportConstraintsMapRef = useRef(airportConstraintsMap || {});
    const [localFlightsMap, setLocalFlightsMap] = useState(() => localFlightsMapRef.current);
    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);
    const [dirtyState, setDirtyState] = useState(EMPTY_DIRTY_STATE);
    const [pendingChangeSet, setPendingChangeSet] = useState(null);

    airportConstraintsMapRef.current = airportConstraintsMap || {};

    // 机场约束到达或更新后，本地工作副本里的派生展示字段（最小过站 / 实际过站）
    // 要跟着重算：机场没配时展示为空，不回到航班上存着的旧数字。
    // 只有确实算出不同结果才写回，避免与父层渲染互相推动。
    useEffect(() => {
        const current = localFlightsMapRef.current;
        if (!current || current.size === 0) {
            return;
        }
        const rebuilt = rebuildLocalDerivedTimings(current, airportConstraintsMapRef.current);
        const changed = Array.from(rebuilt.entries()).some(([flightId, flight]) => {
            const before = current.get(flightId);
            return !before
                || before.min_ground_time_minutes !== flight.min_ground_time_minutes
                || before.ground_time_minutes !== flight.ground_time_minutes;
        });
        if (!changed) {
            return;
        }
        localFlightsMapRef.current = rebuilt;
        setLocalFlightsMap(rebuilt);
    }, [airportConstraintsMap]);

    const rebuildDerivedTimings = useCallback(
        (nextLocalFlightsMap) => rebuildLocalDerivedTimings(nextLocalFlightsMap, airportConstraintsMapRef.current),
        [],
    );

    const replaceLocalState = useCallback((nextLocalFlightsMap) => {
        const result = computeDirtyState(rebuildDerivedTimings(nextLocalFlightsMap), serverStateRef.current);
        localFlightsMapRef.current = result.localFlightsMap;
        setLocalFlightsMap(result.localFlightsMap);
        setDirtyState(result.dirtyState);
        return result;
    }, [rebuildDerivedTimings]);

    const initFromServer = useCallback((flights = []) => {
        const serverFlightsMap = buildFrozenServerMap(flights);
        const nextLocalFlightsMap = rebuildDerivedTimings(buildFlightsMap(flights, serverFlightsMap));
        serverStateRef.current = serverFlightsMap;
        localFlightsMapRef.current = nextLocalFlightsMap;
        setLocalFlightsMap(nextLocalFlightsMap);
        setUndoStack([]);
        setRedoStack([]);
        setDirtyState(EMPTY_DIRTY_STATE);
        setPendingChangeSet(null);
    }, [rebuildDerivedTimings]);

    const applyChangeSet = useCallback(
        (changeSet) => {
            if (!changeSet?.changes?.length) {
                return;
            }

            const nextMap = rebuildDerivedTimings(applyChangeSetValues(localFlightsMapRef.current, changeSet, "after"));
            const result = computeDirtyState(nextMap, serverStateRef.current);
            localFlightsMapRef.current = result.localFlightsMap;
            setLocalFlightsMap(result.localFlightsMap);
            setDirtyState(result.dirtyState);
            setUndoStack((currentStack) => pushUndoStack(currentStack, changeSet));
            setRedoStack([]);
        },
        [rebuildDerivedTimings],
    );

    const undo = useCallback(() => {
        if (!undoStack.length) {
            return null;
        }

        const undoneChangeSet = undoStack[undoStack.length - 1];
        setUndoStack(undoStack.slice(0, -1));

        const nextMap = rebuildDerivedTimings(applyChangeSetValues(localFlightsMapRef.current, undoneChangeSet, "before"));
        const result = computeDirtyState(nextMap, serverStateRef.current);
        localFlightsMapRef.current = result.localFlightsMap;
        setLocalFlightsMap(result.localFlightsMap);
        setDirtyState(result.dirtyState);
        setRedoStack((currentStack) => [...currentStack, undoneChangeSet]);
        return undoneChangeSet;
    }, [rebuildDerivedTimings, undoStack]);

    const redo = useCallback(() => {
        if (!redoStack.length) {
            return null;
        }

        const redoneChangeSet = redoStack[redoStack.length - 1];
        setRedoStack(redoStack.slice(0, -1));

        const nextMap = rebuildDerivedTimings(applyChangeSetValues(localFlightsMapRef.current, redoneChangeSet, "after"));
        const result = computeDirtyState(nextMap, serverStateRef.current);
        localFlightsMapRef.current = result.localFlightsMap;
        setLocalFlightsMap(result.localFlightsMap);
        setDirtyState(result.dirtyState);
        setUndoStack((currentStack) => pushUndoStack(currentStack, redoneChangeSet));
        return redoneChangeSet;
    }, [rebuildDerivedTimings, redoStack]);

    const rollbackToChangeSet = useCallback((changeSetId) => {
        if (pendingChangeSet || !changeSetId || !undoStack.length) {
            return null;
        }

        const targetIndex = undoStack.findIndex((changeSet) => String(changeSet?.id) === String(changeSetId));
        if (targetIndex < 0) {
            return null;
        }

        const targetChangeSet = undoStack[targetIndex];
        const changeSetsToUndo = undoStack.slice(targetIndex + 1);
        if (!changeSetsToUndo.length) {
            return {
                targetChangeSet,
                rolledBackChangeSets: [],
            };
        }

        let nextMap = localFlightsMapRef.current;
        const rolledBackChangeSets = [...changeSetsToUndo].reverse();
        rolledBackChangeSets.forEach((changeSet) => {
            nextMap = applyChangeSetValues(nextMap, changeSet, "before");
        });

        const result = computeDirtyState(rebuildDerivedTimings(nextMap), serverStateRef.current);
        localFlightsMapRef.current = result.localFlightsMap;
        setLocalFlightsMap(result.localFlightsMap);
        setDirtyState(result.dirtyState);
        setUndoStack(undoStack.slice(0, targetIndex + 1));
        setRedoStack((currentStack) => [...currentStack, ...rolledBackChangeSets]);

        return {
            targetChangeSet,
            rolledBackChangeSets,
        };
    }, [pendingChangeSet, rebuildDerivedTimings, undoStack]);

    const getLocalDraft = useCallback(
        (serverDraft) => {
            if (!serverDraft) {
                return serverDraft;
            }

            const localFlights = localFlightsMap.size ? Array.from(localFlightsMap.values()) : serverDraft.flights || [];

            return {
                ...serverDraft,
                flights: localFlights,
                tracks: applyLocalFlightsToTracks(serverDraft.tracks, localFlights),
            };
        },
        [localFlightsMap],
    );

    const getDirtyFlights = useCallback(
        () => Array.from(dirtyState.dirtyFlightIds).map((flightId) => localFlightsMap.get(String(flightId))).filter(Boolean),
        [dirtyState.dirtyFlightIds, localFlightsMap],
    );

    const addLocalFlight = useCallback(
        (flightData = {}) => {
            const id = flightData.id || createLocalFlightId();
            const flight = {
                ...clonePlain(flightData),
                id,
                change_status: ADDED_CHANGE_STATUS,
                change_source: flightData.change_source || "USER",
                _dirty: true,
                _dirtyFields: new Set(getAllFlightFields(flightData)),
            };

            const nextMap = new Map(localFlightsMapRef.current);
            nextMap.set(String(id), flight);
            const result = computeDirtyState(rebuildDerivedTimings(nextMap), serverStateRef.current);
            localFlightsMapRef.current = result.localFlightsMap;
            setLocalFlightsMap(result.localFlightsMap);
            setDirtyState(result.dirtyState);
            setRedoStack([]);
            return flight;
        },
        [rebuildDerivedTimings],
    );

    const deleteLocalFlight = useCallback((flightId) => {
        if (flightId === undefined || flightId === null) {
            return null;
        }

        const flightIdStr = String(flightId);
        let deletedFlight = null;
        const existing = localFlightsMapRef.current.get(flightIdStr);
        if (!existing) {
            return null;
        }

        deletedFlight = {
            ...existing,
            change_status: DELETED_CHANGE_STATUS,
            _dirty: true,
        };
        const nextMap = new Map(localFlightsMapRef.current);
        nextMap.set(flightIdStr, deletedFlight);
        const result = computeDirtyState(rebuildDerivedTimings(nextMap), serverStateRef.current);
        localFlightsMapRef.current = result.localFlightsMap;
        setLocalFlightsMap(result.localFlightsMap);
        setDirtyState(result.dirtyState);
        setRedoStack([]);
        return deletedFlight;
    }, [rebuildDerivedTimings]);

    const resetAfterSave = useCallback(
        (updatedFlights = []) => {
            initFromServer(Array.isArray(updatedFlights) ? updatedFlights : updatedFlights?.flights || []);
        },
        [initFromServer],
    );

    const adoptCommittedChangeSet = useCallback((updatedFlights = [], changeSet) => {
        const flights = Array.isArray(updatedFlights) ? updatedFlights : updatedFlights?.flights || [];
        const serverFlightsMap = buildFrozenServerMap(flights);
        const nextLocalFlightsMap = rebuildDerivedTimings(buildFlightsMap(flights, serverFlightsMap));
        serverStateRef.current = serverFlightsMap;
        localFlightsMapRef.current = nextLocalFlightsMap;
        setLocalFlightsMap(nextLocalFlightsMap);
        setDirtyState(EMPTY_DIRTY_STATE);
        setUndoStack(changeSet?.changes?.length ? (currentStack) => pushUndoStack(currentStack, changeSet) : []);
        setRedoStack([]);
        setPendingChangeSet(null);
    }, [rebuildDerivedTimings]);

    const setPendingPreview = useCallback((changeSet) => {
        setPendingChangeSet(changeSet || null);
    }, []);

    const clearPreview = useCallback(() => {
        setPendingChangeSet(null);
    }, []);

    return useMemo(
        () => ({
            localFlightsMap,
            localFlightsMapRef,
            serverStateRef,
            undoStack,
            redoStack,
            dirtyState,
            pendingChangeSet,
            initFromServer,
            applyChangeSet,
            undo,
            redo,
            rollbackToChangeSet,
            getLocalDraft,
            getDirtyFlights,
            addLocalFlight,
            deleteLocalFlight,
            resetAfterSave,
            adoptCommittedChangeSet,
            setPendingPreview,
            clearPreview,
            replaceLocalState,
        }),
        [
            localFlightsMap,
            undoStack,
            redoStack,
            dirtyState,
            pendingChangeSet,
            initFromServer,
            applyChangeSet,
            undo,
            redo,
            rollbackToChangeSet,
            getLocalDraft,
            getDirtyFlights,
            addLocalFlight,
            deleteLocalFlight,
            resetAfterSave,
            adoptCommittedChangeSet,
            setPendingPreview,
            clearPreview,
            replaceLocalState,
        ],
    );
}

export default useLocalEditState;
